import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { destinations, spaceName } from "@/lib/format";

const LABEL = { private: "Private", family: "Family", guest: "Guest", admin: "Admin" };

export const waitForJob = (jobId, onTick) => new Promise((resolve) => {
  const t = setInterval(async () => {
    const { data } = await api.get(`/move-jobs/${jobId}`);
    onTick?.(data);
    if (data.status === "done") { clearInterval(t); resolve(data); }
  }, 1500);
});

export const resultText = (verb, r) => {
  const others = r.skipped.filter((s) => s.reason === "owned by someone else").length;
  const other = r.skipped.length - others;
  return [`${verb} ${r.done}`, r.failed ? `${r.failed} failed` : null, others ? `${others} photo${others === 1 ? "" : "s"} owned by others ${others === 1 ? "was" : "were"} skipped` : null,
    other ? `${other} skipped (${r.skipped.find((s) => s.reason !== "owned by someone else").reason})` : null].filter(Boolean).join(" · ");
};

const mix = (items) => {
  const n = {};
  items.forEach((i) => { n[i.space] = (n[i.space] || 0) + 1; });
  return Object.entries(n).map(([k, c]) => `${c} ${LABEL[k] || "Other"}`).join(", ");
};

export const MoveDialog = ({ request, onClose, onDone }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  useEffect(() => { if (request) { setBusy(false); setJob(null); } }, [request]);
  if (!request) return null;
  const { action, items } = request;
  const verb = action === "move" ? "Moved" : "Copied";
  const go = async (target, label) => {
    setBusy(true);
    try {
      const { data } = await api.post("/items/bulk", { ids: items.map((i) => i.id), action, target_space: target });
      let r = data;
      if (data.job_id) {
        setJob({ pos: 0, total: data.total });
        const fin = await waitForJob(data.job_id, setJob);
        r = { ...data, done: fin.done, failed: fin.failed };
      }
      toast(resultText(`${verb} to ${label || spaceName(target)}:`, r));
      onDone(r);
      onClose();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); setJob(null); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md bg-[#f4f3f0]" data-testid="move-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">{action === "move" ? "Move" : "Copy"} {items.length} photo{items.length === 1 ? "" : "s"} to:</DialogTitle>
          <DialogDescription data-testid="move-dialog-mix">Now: {mix(items)}. Only photos you own {user?.role === "admin" ? "(or any shared photo, as admin) " : ""}are {action === "move" ? "moved" : "copied"}.</DialogDescription>
        </DialogHeader>
        {job ? (
          <div data-testid="move-dialog-progress">
            <Progress value={job.total ? (job.pos / job.total) * 100 : 0} className="h-2" />
            <p className="text-sm text-neutral-600 mt-2">{job.pos} of {job.total}…</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {destinations().map(([k, l]) => (
              <button key={k} className="btn-outline w-full !justify-start" disabled={busy} onClick={() => go(k)} data-testid={`move-target-${k}`}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : null} {l}
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
